/**
 * Routes de l'application interne : chaque domaine déclare les siennes dans
 * `features/<domaine>/routes.tsx`, `app/routes.tsx` les réunit en une seule
 * arborescence. Une route n'existe pour un compte que s'il a le droit de s'en
 * servir : l'adresse d'un écran qui répondrait 403 montre l'état « Accès refusé ».
 * Seules les anciennes adresses renommées redirigent. L'API vérifie toujours chaque droit.
 */
import type { ReactNode } from 'react';
import type { Capability, SpecializedModule } from '@myequip/shared';

export interface RouteGate {
  /** Une seule de ces capacités suffit. */
  anyOf?: readonly Capability[];
  /** Toutes ces capacités, en plus (une proposition chiffrée : prix de vente, Q82). */
  allOf?: readonly Capability[];
  /** Module métier que l'entreprise doit avoir activé. */
  module?: SpecializedModule;
}

export interface AppRoute {
  path: string;
  element: ReactNode;
  gate?: RouteGate;
}

/** Ce qui décide de l'accès : les capacités effectives et les modules de l'entreprise. */
export interface RouteAccess {
  can: (capability: Capability) => boolean;
  modules: readonly SpecializedModule[];
}

export function routeAllowed(gate: RouteGate | undefined, access: RouteAccess): boolean {
  if (!gate) return true;
  if (gate.module && !access.modules.includes(gate.module)) return false;
  if (gate.allOf && !gate.allOf.every((capability) => access.can(capability))) return false;
  return !gate.anyOf || gate.anyOf.some((capability) => access.can(capability));
}

/**
 * Route servie HORS de la session interne (pages publiques, portail client,
 * espace sous-traitant, console) : chaque espace gère sa propre session ou son
 * lien, et le serveur décide par jeton. Déclarée dans
 * `features/<domaine>/routes.tsx`, réunie par `app/external-routes.tsx`.
 */
export interface ExternalRoute {
  path: string;
  element: ReactNode;
}
